import {
  BalanceData, GearItem, OfferScreen, PaidReward, PassLevel, PassTrack, PassXpSource, SeasonJson, SeasonPass, SeasonsData, TrackReward,
  battleXp, cosmeticSkinPay, eventAt, metaSeed, passUnlocked, payReward, purchasesAllowed, seasonAt, seasonLive, seasonTheme,
} from "@highmarch/domain";
import type { LevelCatalog } from "./levelCatalog";
import { NOT_SAVING_HERE } from "./purchases";
import { gameEvents } from "./gameEvents";
import { HasSections, registerSaveSection } from "./saveSections";

/**
 * The season pass "War Campaign" (MP-22) as use cases: the `season` save section, XP from play (battles, Daily Orders,
 * event nodes, the Daily Road through `awardPassXp`), claims, the rollover to a new season and the pass purchases the
 * Store delivers (PurchaseService routes them here through its product handlers). The rules are @highmarch/domain
 * meta/season.ts; the screen's view is seasonView.ts.
 *
 * Rollover (owner default, MP-22): when a new season starts, the ended season's earned, unclaimed rewards are PAID
 * automatically (nothing earned is ever lost) and listed once on a summary card in the Pass sheet.
 */

function asObject<T>(v: unknown): T | undefined {
  return typeof v === "object" && v !== null && !Array.isArray(v) ? (v as T) : undefined;
}

export const seasonSection = registerSaveSection<SeasonJson>("season", asObject, (v) => v);

/**
 * The clock the pass reads (MP-13): the device clock, floored by the newest signed store transaction's date
 * (`Wallet.trustedNow`), so moving the clock back cannot re-open an earlier season. The saved season is a second floor.
 */
export function trustedSeasonNow(catalog: LevelCatalog, deviceNow: number): number {
  return catalog.economy.wallet.trustedNow(deviceNow);
}

/** Seed tag of a pass reward's chest (apart from every other meta roll). */
const PASS_CHEST_TAG = "passChest";

/** A paid pass reward: what it paid, the gear its chest held, and the cosmetic it unlocked ("" for none). */
export interface PassClaim {
  readonly track: PassTrack;
  readonly tier: number;
  readonly paid: PaidReward;
  readonly gear: readonly GearItem[];
  readonly title: string;
  readonly skin: string;
  /**
   * Gems the cosmetic tier paid instead of the season's skin, already owned (a later loop of the season list:
   * seasons.json repeatSkinGems; included in `paid.gems`); 0 otherwise.
   */
  readonly skinGems: number;
  /** The owned skin those gems stand in for ("" when none). */
  readonly ownedSkin: string;
}

/** Cosmetics the pass pays: a skin (the cosmetics feature) and commander titles (the achievements' granted titles). */
export interface PassCosmetics {
  ownsSkin(id: string): boolean;
  unlockSkin(id: string): void;
}

export class Season {
  constructor(private readonly catalog: LevelCatalog, readonly state: SeasonPass, private readonly cosmetics: PassCosmetics | null = null) {}

  static stateFromSave(balance: BalanceData, save: HasSections): SeasonPass { return SeasonPass.fromJson(balance.seasons, seasonSection.read(save)); }
  applyTo(save: HasSections): void { seasonSection.write(save, this.state.toJson()); }

  get data(): SeasonsData { return this.catalog.balance.seasons; }
  /** The pass is open for this player (its unlock level cleared). */
  get open(): boolean { return passUnlocked(this.data, this.catalog.highestLevelCleared); }
  /** The trusted time (`trustedSeasonNow`). */
  now(deviceNow: number): number { return trustedSeasonNow(this.catalog, deviceNow); }
  /** A season is running at `now`. */
  live(now: number): boolean { return seasonLive(this.data, seasonAt(this.data, this.now(now))); }

  /**
   * Brings the pass to `now`: a new season pays the ended one's earned rewards (returned for the summary toast), and
   * Daily Orders and event nodes claimed since the last look are credited. Cheap; called on every hub render.
   */
  sync(now: number): PassClaim[] {
    const t = this.now(now);
    const r = this.state.rollover(this.data, t);
    const paid = r ? r.rewards.map((x) => this.pay(x, r.ended, t)) : [];
    if (this.state.pass === "plus") this.grantTitle(seasonTheme(this.data, this.state.season).plusTitle);
    if (this.open && this.state.season >= 0) {
      for (const o of this.catalog.orders.orders) if (o.claimed) this.state.addXp(this.data, "order", `o${o.uid}`, this.data.xp.order);
      const e = eventAt(this.catalog.balance.events, t);
      if (e) e.nodes.forEach((_, i) => { if (this.catalog.endgame.events.isClaimed(e.id, i)) this.state.addXp(this.data, "eventNode", `${e.id}.${i}`, this.data.xp.eventNode); });
    }
    return paid;
  }

  /** XP for one thing the player did (`key`: what makes it count once). 0 before the pass opens or a season runs. */
  award(source: PassXpSource, key: string, now: number, amount = this.data.xp[source]): number {
    this.sync(now);
    if (!this.open) return 0;
    return this.state.addXp(this.data, source, key, amount);
  }

  /** A finished battle: a win, or a loss past the stage floor (an abandoned run earns nothing). */
  awardBattle(b: { readonly won: boolean; readonly lost: boolean; readonly stagesCleared: number; readonly key: string }, now: number): number {
    const xp = battleXp(this.data, b);
    return xp > 0 ? this.award(b.won ? "win" : "loss", b.key, now, xp) : 0;
  }

  /** Reached, unclaimed rewards now (the hub button's red dot). */
  claimable(now: number): number {
    this.sync(now);
    return this.open ? this.state.claimable(this.data).length : 0;
  }

  /** Claims one reward (hub only): pays it, banks its chest, unlocks its cosmetic. A string says why not. */
  claim(track: PassTrack, tier: number, now: number, screen: OfferScreen = "hub"): PassClaim | string {
    if (!purchasesAllowed(screen)) return "not here";
    if (!this.catalog.canPersist()) return NOT_SAVING_HERE;
    this.sync(now);
    if (!this.open) return "not open";
    const r = this.state.claim(this.data, track, tier);
    return r ? this.pay(r, this.state.season, this.now(now)) : "not ready";
  }

  /** Claims every reached reward (hub only). */
  claimAll(now: number, screen: OfferScreen = "hub"): PassClaim[] | string {
    if (!purchasesAllowed(screen)) return "not here";
    if (!this.catalog.canPersist()) return NOT_SAVING_HERE;
    this.sync(now);
    if (!this.open) return "not open";
    return this.state.claimable(this.data).map((r) => { this.state.claim(this.data, r.track, r.tier); return this.pay(r, this.state.season, this.now(now)); });
  }

  /** The rollover summary was shown: it goes. */
  dismissSummary(): void { this.state.summary = null; }

  private pay(r: TrackReward, season: number, at: number): PassClaim {
    const c = this.catalog;
    // the cosmetic tier's skin, or its gems when it is owned already (decided before paying: the gems go in with the rest)
    const theme = seasonTheme(this.data, season);
    const cosmetic = r.cosmetic && r.track === "premium" && this.cosmetics ? cosmeticSkinPay(this.data, theme, this.cosmetics.ownsSkin(theme.skin)) : { skin: "", gems: 0 };
    const reward = cosmetic.gems > 0 ? { ...r.reward, gems: r.reward.gems + cosmetic.gems } : r.reward;
    const paid = payReward(c.balance, reward, {
      profile: c.profile, wallet: c.economy.wallet, level: Math.max(1, c.highestLevelCleared),
      seed: metaSeed(c.playerId, `${PASS_CHEST_TAG}:${season}:${r.track}`, r.tier), at, ref: `pass.${season + 1}.${r.track}.${r.tier}`,
    });
    let title = "";
    if (cosmetic.skin) this.cosmetics?.unlockSkin(cosmetic.skin);
    if (r.cosmetic && r.track === "premium" && this.grantTitle(theme.title)) title = theme.title;
    const gear = paid.chest ? c.bankChest(paid.chest) : [];
    // Premium is bought with money: its chests' gear is bound (never resold for Marks), as a Deals chest's is.
    if (r.track === "premium") for (const g of gear) g.bound = true;
    return { track: r.track, tier: r.tier, paid, gear, title, skin: cosmetic.skin, skinGems: cosmetic.gems, ownedSkin: cosmetic.gems > 0 ? theme.skin : "" };
  }

  private grantTitle(title: string): boolean { return title !== "" && this.catalog.achievements.progress.grantTitle(title); }

  // ------------------------------------------------------------------ purchases (PurchaseService product handler)

  /** The pass a store product sells, or null when it is not a pass product. */
  levelOf(productId: string): Exclude<PassLevel, "free"> | null {
    if (productId === this.data.premium.price.product && productId !== "") return "premium";
    if (productId === this.data.premiumPlus.price.product && productId !== "") return "plus";
    return null;
  }
  knowsProduct(productId: string): boolean { return this.levelOf(productId) !== null; }

  /** Whether `productId` can be bought now (hub only, a season running, no pass yet this season): true, or why not. */
  prepare(productId: string, now: number, screen: OfferScreen = "hub"): true | string {
    if (!purchasesAllowed(screen)) return "not here";
    const level = this.levelOf(productId);
    if (!level) return "no such product";
    if (!this.catalog.canPersist()) return NOT_SAVING_HERE;
    this.sync(now);
    if (!this.open || !this.live(now)) return "not on sale";
    // One pass a season: Premium+ is not sold over Premium (a late delivery of both still upgrades: SeasonPass.applyPurchase).
    if (this.state.pass !== "free") return "already owned";
    return true;
  }

  /**
   * A verified pass transaction, once: applied to the running season (or banked for the next when it already has one),
   * with its VIP XP; recorded in the wallet's purchases (0 gems) so a refund that came first grants nothing. The
   * caller (PurchaseService) persists, then finishes it.
   */
  grant(t: { readonly transactionId: string; readonly productId: string; readonly purchasedAtMs: number }, now: number): "granted" | "duplicate" | "unknownProduct" {
    const level = this.levelOf(t.productId);
    if (!level) return "unknownProduct";
    const wallet = this.catalog.economy.wallet;
    if (this.state.hasTxn(t.transactionId) || wallet.purchase(t.transactionId) !== null) return "duplicate";
    this.sync(now);
    if (this.state.applyPurchase(this.data, level, t.transactionId) === "duplicate") return "duplicate";
    const price = level === "plus" ? this.data.premiumPlus.price : this.data.premium.price;
    this.catalog.deals.notePurchase(price.cents);
    const at = Number.isFinite(t.purchasedAtMs) ? Math.floor(t.purchasedAtMs / 1000) : now;
    wallet.recordPurchase(t.transactionId, t.productId, 0, at);
    this.sync(now);   // a Premium+ applied now gives its title
    return "granted";
  }

  /** A refunded pass transaction: the pass it bought goes (claimed rewards stay, as spent gems do). */
  revoke(transactionId: string): boolean { return this.state.revoke(transactionId); }
}

// ------------------------------------------------------------------ the XP hook other features call

type PassXpSink = (source: PassXpSource, key: string) => number;
let sink: PassXpSink | null = null;

/** The web's season feature installs the live pass here at boot (null: no pass, every award is 0). */
export function setPassXpSink(s: PassXpSink | null): void { sink = s; }

/**
 * Pass XP for something a feature resolved (MP-20's Daily Road: `awardPassXp("dailyRoad", String(utcDay))`). `key`
 * makes it count once (letters, digits, `_ . : -`). Returns the XP added; the caller persists as it does after any grant.
 */
export function awardPassXp(source: PassXpSource, key: string): number { return sink ? sink(source, key) : 0; }

/** MP-20 → MP-22: the day's paid Daily Road run (its first win) earns the pass its Daily Road XP, once per UTC day. */
gameEvents.on("dailyRoadCompleted", (e) => { if (e.rewarded) awardPassXp("dailyRoad", String(e.day)); });
